import React, { useState } from "react";
import clsx from "clsx";
import { t } from "ttag";
import { useDropzone } from "react-dropzone";
import { BaseDialog, Button, CloudUploadIcon, DeleteIcon, ExcelIcon, Spinner, Typography } from "@coolremote/shared";
import {
  IDeviceInfoForExport,
  IParsedModbusDeviceFile,
  ModbusFileStructureError,
  downloadModbusDeviceSampleTemplate,
  parseModbusDeviceFile,
  summarizeImportMessages,
} from "../modbusDeviceFileIO";
import { IModbusDeviceUnit } from "../../types";
import styles from "./ImportModbusDeviceModal.module.css";

const ACCEPTED_EXTENSIONS = [".xlsx"];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export interface IImportedModbusDevice {
  device: Partial<IDeviceInfoForExport>;
  units: IModbusDeviceUnit[];
}

interface IProps {
  open: boolean;
  /** Whether the device being imported into is on a TCP line — Host/Port are only required in that case. */
  isTcp: boolean;
  onClose: () => void;
  onImport: (result: IImportedModbusDevice) => void;
}

const ImportModbusDeviceModal: React.FC<IProps> = ({ open, isTcp, onClose, onImport }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [parsed, setParsed] = useState<IParsedModbusDeviceFile | null>(null);

  const resetFileState = () => {
    setFile(null);
    setErrorText("");
    setParsed(null);
  };

  const handleFile = async (droppedFile: File) => {
    setErrorText("");
    setParsed(null);

    if (droppedFile.size > MAX_FILE_SIZE_BYTES) {
      setErrorText(t`File size exceeds the maximum allowed size (5MB).`);
      setFile(null);
      return;
    }
    const extension = droppedFile.name.slice(droppedFile.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setErrorText(t`Unsupported file type. Please upload a .xlsx file.`);
      setFile(null);
      return;
    }

    setFile(droppedFile);
    setIsParsing(true);
    try {
      const result = await parseModbusDeviceFile(droppedFile, isTcp);
      setParsed(result);
    } catch (err) {
      setErrorText(err instanceof ModbusFileStructureError ? err.message : t`Could not read the file.`);
      setFile(null);
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) {
      setErrorText(t`No file was uploaded.`);
      return;
    }
    handleFile(droppedFile);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: ACCEPTED_EXTENSIONS, maxFiles: 1 });

  const handleConfirm = () => {
    if (!parsed) {
      return;
    }
    onImport({ device: parsed.device, units: parsed.units });
    resetFileState();
    onClose();
  };

  const parameterCount = parsed?.units.reduce((sum, u) => sum + u.rows.length, 0) ?? 0;

  const handleClose = () => {
    resetFileState();
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      staticBackdrop
      disabled={isParsing}
      title={t`Import Modbus Device Information`}
      className={styles.dialogRoot}
      style={{ width: "min(700px, 90vw)", maxWidth: "none" }}
      paperProps={{ style: { width: "100%", height: "600px", maxHeight: "600px" } }}
      cancelLabel={t`Cancel`}
      confirmLabel={t`Import`}
      confirmDisabled={!parsed}
      loading={isParsing}
      onConfirm={handleConfirm}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.headerText}>
          <Typography className={styles.subtitleText}>
            {t`Need help formatting your file?`}{" "}
            <button type="button" className={styles.sampleTemplateLink} onClick={downloadModbusDeviceSampleTemplate}>
              {t`Download sample template`}
            </button>
          </Typography>
        </div>

        <div className={styles.dropzoneContainer}>
          {!file ? (
            <div {...getRootProps()} className={styles.uploadInput}>
              <input {...getInputProps()} disabled={isParsing} />
              <CloudUploadIcon width={32} height={32} className={styles.uploadIcon} />
              <Typography className={styles.dragText}>{t`Drag and drop a file here, or`}</Typography>
              <Button className={styles.chooseFileBtn}>{t`Choose file`}</Button>
              <Typography className={styles.limitsText}>{t`XLSX, up to 5MB, 1,000 rows max`}</Typography>
              {errorText && <Typography className={styles.errorText}>{errorText}</Typography>}
            </div>
          ) : (
            <div className={styles.fileAndResultsWrapper}>
              <div className={styles.fileInfoBox}>
                <div className={styles.fileInfoLeft}>
                  <ExcelIcon width={24} height={24} className={styles.fileIcon} />
                  <div>
                    <Typography className={styles.fileName}>{file.name}</Typography>
                    <Typography className={styles.fileDetails}>
                      {parsed
                        ? t`${parsed.units.length} functional unit(s), ${parameterCount} parameter(s) detected`
                        : ""}{" "}
                      · {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </div>
                </div>
                {!isParsing && (
                  <button className={styles.removeFileBtn} onClick={resetFileState}>
                    <DeleteIcon width={18} height={18} color="#a09ba0" />
                  </button>
                )}
              </div>

              {errorText && <Typography className={styles.errorText}>{errorText}</Typography>}

              {isParsing && (
                <div className={styles.importingRow}>
                  <Typography className={styles.summaryText}>{t`Reading file…`}</Typography>
                  <div className={styles.importingIndicator}>
                    <Spinner size={16} />
                  </div>
                </div>
              )}

              {parsed && parsed.messages.length > 0 && (
                <div className={styles.validationSection}>
                  <div className={styles.importingRow}>
                    <div>
                      <Typography className={styles.validationTitle}>{t`Validation results`}</Typography>
                      <Typography className={styles.summaryText}>
                        {summarizeImportMessages(parsed.messages)}
                      </Typography>
                    </div>
                  </div>
                  <div className={styles.resultsContainer}>
                    {parsed.messages.map((item, index) => (
                      <div
                        key={index}
                        className={clsx(styles.resultRow, {
                          [styles.resultGood]: item.type === "good",
                          [styles.resultWarn]: item.type === "warning",
                          [styles.resultError]: item.type === "error",
                        })}
                      >
                        <Typography className={styles.resultText}>{item.message}</Typography>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </BaseDialog>
  );
};

export default ImportModbusDeviceModal;
